import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { categoryDocumentsApi, DocumentCategory, CategoryDocument } from '../services/api';

export default function Resources() {
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [documents, setDocuments] = useState<CategoryDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mediaPlayer, setMediaPlayer] = useState<{ url: string; type: 'audio' | 'video'; filename: string } | null>(null);

  // Load categories on mount
  useEffect(() => {
    async function loadCategories() {
      try {
        const cats = await categoryDocumentsApi.getCategories();
        setCategories(cats);

        // Auto-select "Default" category if available
        const defaultCat = cats.find(
          (c) => c.categoryid === 'Default' || c.categoryname.toLowerCase() === 'default'
        );
        if (defaultCat) {
          setSelectedCategory(String(defaultCat.categoryid));
        } else if (cats.length > 0) {
          setSelectedCategory(String(cats[0].categoryid));
        }
      } catch (err) {
        console.error('Failed to load categories:', err);
        setError('Failed to load document categories');
      } finally {
        setIsLoading(false);
      }
    }
    loadCategories();
  }, []);

  // Load documents when category changes
  useEffect(() => {
    if (!selectedCategory) return;

    async function loadDocuments() {
      setIsLoadingDocs(true);
      setError(null);
      try {
        const response = await categoryDocumentsApi.getDocuments(selectedCategory);
        setDocuments(response.data || []);
      } catch (err) {
        console.error('Failed to load documents:', err);
        setError('Failed to load documents');
        setDocuments([]);
      } finally {
        setIsLoadingDocs(false);
      }
    }
    loadDocuments();
  }, [selectedCategory]);

  // Check if file is a media type
  const isMediaFile = (filename: string): 'audio' | 'video' | null => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext || '')) return 'audio';
    if (['mp4', 'webm', 'mov', 'avi', 'm4v'].includes(ext || '')) return 'video';
    return null;
  };

  // Browser-renderable types open in a NEW window via the self-authenticating
  // SAS URL, so the app stays usable while the rep references the document
  // (#2118). Office docs etc. keep the download path — browsers can't render
  // them inline anyway.
  const isViewableFile = (filename: string): boolean => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'txt'].includes(ext || '');
  };

  // Fallback download path (also used when a popup gets blocked).
  const downloadDocument = async (doc: CategoryDocument) => {
    const blob = await categoryDocumentsApi.downloadDocument(doc.fileid);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.filename || `document_${doc.fileid}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Handle document click - view in new window, play media, or download.
  const handleDocumentClick = async (doc: CategoryDocument) => {
    const mediaType = isMediaFile(doc.filename);

    if (mediaType) {
      // Stream media file and open player modal
      setDownloadingId(doc.fileid);
      try {
        const streamUrl = await categoryDocumentsApi.streamDocument(doc.fileid);
        setMediaPlayer({ url: streamUrl, type: mediaType, filename: doc.filename });
      } catch (err) {
        console.error('Stream failed:', err);
        setError('Failed to load media file');
      } finally {
        setDownloadingId(null);
      }
    } else if (isViewableFile(doc.filename)) {
      // Open viewable docs (PDF/images) in a NEW window so the app keeps
      // running behind it. The window MUST be opened synchronously inside the
      // tap handler — popup blockers (Safari especially) kill window.open
      // calls made after an await. We open it blank, then point it at the
      // SAS URL once minted; the SAS carries inline Content-Disposition so
      // the browser renders instead of downloading.
      const viewer = window.open('', '_blank');
      setDownloadingId(doc.fileid);
      try {
        const streamUrl = await categoryDocumentsApi.streamDocument(doc.fileid);
        if (viewer && !viewer.closed) {
          viewer.location.replace(streamUrl);
        } else {
          // Popup blocked (or already closed) — fall back to download.
          await downloadDocument(doc);
        }
      } catch (err) {
        console.error('View failed:', err);
        viewer?.close();
        setError('Failed to open document');
      } finally {
        setDownloadingId(null);
      }
    } else {
      // Download everything else (Office docs etc.)
      setDownloadingId(doc.fileid);
      try {
        await downloadDocument(doc);
      } catch (err) {
        console.error('Download failed:', err);
        setError('Failed to download document');
      } finally {
        setDownloadingId(null);
      }
    }
  };

  // Get file icon based on filename
  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext || '')) {
      return (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 18a.5.5 0 01-.5-.5v-2a.5.5 0 01.5-.5h1a1.5 1.5 0 010 3h-1zm1-1a.5.5 0 000-1h-.5v1h.5zm2.5 1a.5.5 0 01-.5-.5v-2a.5.5 0 01.5-.5h1a1.5 1.5 0 011.5 1.5 1.5 1.5 0 01-1.5 1.5h-1zm1-1a.5.5 0 00.5-.5.5.5 0 00-.5-.5h-.5v1h.5zm3.5 1h-1a.5.5 0 01-.5-.5v-2a.5.5 0 011 0v.5h.5a.5.5 0 010 1h-.5v.5a.5.5 0 01.5.5z" />
        </svg>
      );
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) {
      return (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext || '')) {
      return (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      );
    }
    if (['mp4', 'webm', 'mov', 'avi', 'm4v'].includes(ext || '')) {
      return (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      );
    }
    if (['doc', 'docx'].includes(ext || '')) {
      return (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
    if (['xls', 'xlsx'].includes(ext || '')) {
      return (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
    // Default file icon
    return (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  };

  const getFileColor = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'bg-red-100 text-red-600';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) return 'bg-purple-100 text-purple-600';
    if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext || '')) return 'bg-orange-100 text-orange-600';
    if (['mp4', 'webm', 'mov', 'avi', 'm4v'].includes(ext || '')) return 'bg-pink-100 text-pink-600';
    if (['doc', 'docx'].includes(ext || '')) return 'bg-blue-100 text-blue-600';
    if (['xls', 'xlsx'].includes(ext || '')) return 'bg-green-100 text-green-600';
    return 'bg-slate-100 text-slate-600';
  };

  // Get action icon (play for media, open-in-new-window for viewable docs,
  // download for the rest)
  const getActionIcon = (filename: string) => {
    const mediaType = isMediaFile(filename);
    if (mediaType) {
      return (
        <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    if (isViewableFile(filename)) {
      // External-link arrow: opens in a new window (#2118).
      return (
        <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <PageHeader title="Resources" subtitle="Training, forms & documents" backPath="/profile" />
        <div className="flex items-center justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <PageHeader title="Resources" subtitle="Training, forms & documents" backPath="/profile" />

      {/* Category Selector */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <label htmlFor="category" className="mb-2 block text-sm font-medium text-slate-700">
          Document Folder
        </label>
        <select
          id="category"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
        >
          {categories.map((cat) => (
            <option key={cat.categoryid} value={cat.categoryid}>
              {cat.categoryname}
            </option>
          ))}
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-4 mt-4 rounded-xl bg-red-50 p-4 text-red-700">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        </div>
      )}

      {/* Documents List */}
      <main className="p-4">
        {isLoadingDocs ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          </div>
        ) : documents.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-card">
            <svg
              className="mx-auto h-12 w-12 text-slate-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-3 font-semibold text-slate-700">No documents found</h3>
            <p className="mt-1 text-sm text-slate-500">
              Select a different folder to view documents
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <button
                key={doc.fileid}
                onClick={() => handleDocumentClick(doc)}
                disabled={downloadingId === doc.fileid}
                className="flex w-full items-center gap-4 rounded-xl bg-white p-4 text-left shadow-card transition-all hover:shadow-card-hover active:scale-[0.98] disabled:opacity-70"
              >
                <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${getFileColor(doc.filename)}`}>
                  {getFileIcon(doc.filename)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 truncate">{doc.filename}</h3>
                  {doc.description && (
                    <p className="text-sm text-slate-500 truncate">{doc.description}</p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {downloadingId === doc.fileid ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                  ) : (
                    getActionIcon(doc.filename)
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Quick Links */}
        <div className="mt-6 rounded-xl bg-white p-4 shadow-card">
          <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
            Quick Links
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <a
              href="/help"
              className="flex items-center gap-2 rounded-lg border-2 border-primary-200 bg-primary-50 p-3 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
            >
              <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Help Guide
            </a>
            <a
              href="https://rdmerchandising.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              RDM Website
            </a>
            <a
              href="mailto:support@rdmerchandising.com"
              className="flex items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact Support
            </a>
            <a
              href="tel:+18005551234"
              className="flex items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call Office
            </a>
          </div>
        </div>
      </main>

      {/* Media Player Modal */}
      {mediaPlayer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setMediaPlayer(null)}
        >
          <div
            className="relative w-full max-w-3xl rounded-2xl bg-slate-900 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setMediaPlayer(null)}
              className="absolute -right-2 -top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg"
            >
              <svg className="h-6 w-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Title */}
            <h3 className="mb-4 truncate text-center font-medium text-white">
              {mediaPlayer.filename}
            </h3>

            {/* Player */}
            {mediaPlayer.type === 'audio' ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-orange-500">
                  <svg className="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <audio
                  controls
                  autoPlay
                  className="w-full"
                  src={mediaPlayer.url}
                >
                  Your browser does not support the audio element.
                </audio>
              </div>
            ) : (
              <video
                controls
                autoPlay
                className="w-full rounded-lg"
                src={mediaPlayer.url}
              >
                Your browser does not support the video element.
              </video>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
